import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useRef } from 'react';

import { queryClient } from '../queryClient';
import { SessionContext } from 'auth/SessionProvider';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import {
  endCliTerminalSession,
  getCliTerminalCatalog,
  getCliTerminalCommands,
  getCliTerminalSessions,
  getCliTerminalTargets,
  runCliTerminalCommand,
  saveCliTerminalFile,
  startCliTerminalSession,
  startCliTerminalSessionByToken,
  uploadCliTerminalFile,
} from 'services/cliTerminal';
import { getClusterKey } from 'services/support';
import { CliTarget, CliUploadProgress } from 'types/cli';

// The key lives server-side for up to 72h, so this list also restores the tabs after a reload.
export function useCliAuthorizedClusters({ enabled = true } = {}) {
  const { user } = useContext(SessionContext);

  const query = useQuery({
    queryKey: [QueryServerKeys.CLI_TERMINAL.SESSIONS, user?.uuid],
    queryFn: () => getCliTerminalSessions({ userId: user.uuid }),
    enabled: enabled && !!user?.uuid,
    retry: false,
    // The key can expire while the page is open, and a cluster whose token was revoked in the
    // Portal must stop being openable here.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  return {
    sessions: query.data?.data?.sessions ?? [],
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCliCatalog({ enabled = true } = {}) {
  const { user } = useContext(SessionContext);

  const query = useQuery({
    queryKey: [QueryServerKeys.CLI_TERMINAL.CATALOG, user?.uuid],
    queryFn: () => getCliTerminalCatalog({ userId: user.uuid }),
    enabled: enabled && !!user?.uuid,
    retry: false,
    staleTime: 60_000,
  });

  return {
    permission: query.data?.data?.permission,
    commands: query.data?.data?.commands ?? [],
    recipes: query.data?.data?.recipes ?? [],
    isPending: query.isPending,
    error: query.error,
  };
}

export function useGenerateCliToolKey() {
  const { user } = useContext(SessionContext);

  const mutation = useMutation({
    mutationFn: (token: string) =>
      getClusterKey(user.id, token).then(
        response => response.data as { key: string },
      ),
  });

  return {
    generate: mutation.mutate,
    key: mutation.data?.key ?? null,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export const refetchCliAuthorizedClusters = () =>
  queryClient.invalidateQueries({
    queryKey: [QueryServerKeys.CLI_TERMINAL.SESSIONS],
  });

export function useAuthenticateCliCluster() {
  const { user } = useContext(SessionContext);

  const mutation = useMutation({
    mutationKey: [QueryServerKeys.CLI_TERMINAL.AUTHENTICATE],
    // With a cluster name the token must belong to that cluster; without one, the token picks it.
    mutationFn: ({
      instanceName,
      clusterAccessToken,
    }: {
      instanceName?: string | null;
      clusterAccessToken: string;
    }) =>
      instanceName
        ? startCliTerminalSession({
            userId: user.uuid,
            instanceName,
            clusterAccessToken,
          })
        : startCliTerminalSessionByToken({
            userId: user.uuid,
            clusterAccessToken,
          }),
    onSuccess: response => {
      queryClient.setQueryData(
        [QueryServerKeys.CLI_TERMINAL.SESSION, response.data.cluster],
        response,
      );
      refetchCliAuthorizedClusters();
    },
  });

  return {
    authenticate: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useCliTerminal({
  clusterName,
  enabled = true,
}: {
  clusterName: string;
  enabled?: boolean;
}) {
  const { user } = useContext(SessionContext);
  // One in-flight request per pod tab, so Ctrl-C stops only the tab it was pressed in.
  const runningRequests = useRef(new Map<string, AbortController>());

  const session = useQuery({
    queryKey: [QueryServerKeys.CLI_TERMINAL.SESSION, clusterName],
    // No token: the support already pasted it, and this resumes the key that produced.
    queryFn: () =>
      startCliTerminalSession({ userId: user.uuid, instanceName: clusterName }),
    enabled: enabled && !!user?.uuid && !!clusterName,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // `allowed` comes from the CliCommands table this same app edits, so refetch on focus and the
  // padlocks follow an edit made in another tab.
  const commands = useQuery({
    queryKey: [QueryServerKeys.CLI_TERMINAL.COMMANDS, clusterName],
    queryFn: () =>
      getCliTerminalCommands({ userId: user.uuid, instanceName: clusterName }),
    enabled: enabled && !!session.data,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // Not Infinity: the pod list follows the cluster's scale.
  const targets = useQuery({
    queryKey: [QueryServerKeys.CLI_TERMINAL.TARGETS, clusterName],
    queryFn: () =>
      getCliTerminalTargets({ userId: user.uuid, instanceName: clusterName }),
    enabled: enabled && !!session.data,
    retry: false,
    staleTime: 60_000,
  });

  const { mutate: disconnect } = useMutation({
    mutationFn: () =>
      endCliTerminalSession({ userId: user.uuid, instanceName: clusterName }),
    onSuccess: () => {
      // Removed, not refetched: with `staleTime: Infinity` a reopened tab would paint connected and 401.
      [
        QueryServerKeys.CLI_TERMINAL.SESSION,
        QueryServerKeys.CLI_TERMINAL.COMMANDS,
        QueryServerKeys.CLI_TERMINAL.TARGETS,
      ].forEach(key =>
        queryClient.removeQueries({ queryKey: [key, clusterName] }),
      );
      refetchCliAuthorizedClusters();
    },
  });

  const cancel = useCallback((targetId: string) => {
    runningRequests.current.get(targetId)?.abort();
    runningRequests.current.delete(targetId);
  }, []);

  const claim = useCallback((targetId: string) => {
    const controller = new AbortController();
    runningRequests.current.get(targetId)?.abort();
    runningRequests.current.set(targetId, controller);
    return controller;
  }, []);

  const release = useCallback(
    (targetId: string, controller: AbortController) => {
      if (runningRequests.current.get(targetId) === controller) {
        runningRequests.current.delete(targetId);
      }
    },
    [],
  );

  const run = useCallback(
    async (
      target: CliTarget,
      line: string,
      onChunk: (text: string) => void,
      options: { cwd?: string; columns?: number } = {},
    ) => {
      const controller = claim(target.id);
      try {
        return await runCliTerminalCommand({
          userId: user.uuid,
          instanceName: clusterName,
          line,
          target,
          cwd: options.cwd,
          columns: options.columns,
          onChunk,
          signal: controller.signal,
        });
      } finally {
        release(target.id, controller);
      }
    },
    [clusterName, user?.uuid, claim, release],
  );

  // The second half of an upload line, once a file has been chosen.
  const upload = useCallback(
    async (
      target: CliTarget,
      line: string,
      file: File,
      onProgress: (progress: CliUploadProgress) => void,
      options: { cwd?: string } = {},
    ) => {
      const controller = claim(target.id);
      try {
        return await uploadCliTerminalFile({
          userId: user.uuid,
          instanceName: clusterName,
          line,
          target,
          cwd: options.cwd,
          file,
          onProgress,
          signal: controller.signal,
        });
      } finally {
        release(target.id, controller);
      }
    },
    [clusterName, user?.uuid, claim, release],
  );

  // The second half of an edit line, once the file has been changed and saved.
  const saveEdit = useCallback(
    async (target: CliTarget, path: string, content: string) => {
      const controller = claim(target.id);
      try {
        return await saveCliTerminalFile({
          userId: user.uuid,
          instanceName: clusterName,
          path,
          target,
          content,
          signal: controller.signal,
        });
      } finally {
        release(target.id, controller);
      }
    },
    [clusterName, user?.uuid, claim, release],
  );

  return {
    session: session.data?.data,
    isConnecting: session.isPending,
    connectionError: session.error,
    reconnect: session.refetch,
    availableCommands: commands.data?.data?.commands ?? [],
    recipes: commands.data?.data?.recipes ?? [],
    catalogSource: commands.data?.data?.catalog?.source,
    isCommandsLoading: commands.isPending,
    targets: targets.data?.data?.targets ?? [],
    defaultTargetId: targets.data?.data?.defaultId,
    isTargetsLoading: targets.isPending,
    run,
    upload,
    saveEdit,
    cancel,
    disconnect,
  };
}
